import './style.sass';

import React, { FC, useEffect, lazy, Suspense, useState, useRef } from 'react';
import { classNames as cn, retry } from '$utils';

import('lottie-react');
const Lottie = lazy(() => retry(() => import('lottie-react')));

import { LottieIconProps } from './LottieIcon.interface';
import * as Sentry from '@sentry/react';

function isValidUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol);
  } catch {
    return false;
  }
}

function getExtensionFromUrl(value: string): string {
  try {
    return new URL(value).pathname.split('.').pop()!.toLowerCase();
  } catch {
    return '';
  }
}

export const LottieIcon: FC<LottieIconProps> = ({
  size = 120,
  name = '',
  height = 0,
  width = 0,
  autoplay = true,
  loop = false,
  className,
  onClick,
  onComplete,
  fallback,
  play = false,
  initialSegment,
}) => {
  const [lottieData, setLottieData] = useState<any | null>(null);

  useEffect(() => {
    if (isValidUrl(name)) {
      fetch(name)
        .then((response) => response.arrayBuffer())
        .then((data) => {
          try {
            const extension = getExtensionFromUrl(name);
            if (extension === 'lottie') {
              setLottieData(data);
            } else if (extension === 'json') {
              setLottieData(JSON.parse(new TextDecoder().decode(data)));
            } else {
              console.error(`[LottieIcon] Unsupported file type: ${extension}`);
            }
          } catch (error) {
            console.error('[LottieIcon] Error parsing file:', error);
          }
        })
        .catch((e) => {
          console.error(`[LottieIcon] Failed to fetch "${name}"`, e);
        });
    } else {
      import(`../../assets/lottie/${name}.json`)
        .then((e) => {
          setLottieData(e);
        })
        .catch((e) => {
          console.error(`[LottieIcon] Failed to import "${name}"`, e);
        });
    }
  }, [name]);

  const ref = useRef<any>(null);

  useEffect(() => {
    if (play && ref.current) {
      ref.current.goToAndPlay(0);
    }
  }, [play, ref.current]);

  return (
    <div
      className={cn('LottieIcon', className)}
      onClick={onClick}
      style={{
        width: width || size,
        height: height || size,
      }}
    >
      {lottieData ? (
        <Suspense fallback={<div />}>
          <Lottie
            lottieRef={ref}
            animationData={lottieData}
            loop={loop}
            autoplay={autoplay}
            onComplete={onComplete}
            initialSegment={initialSegment}
          />
        </Suspense>
      ) : (
        fallback
      )}
    </div>
  );
};
